import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useState } from "react";
import PayrollDeductionDetails from "../components/PayrollDeductionDetails";
import ButtonWithIcon from "../components/Button";
import ConfirmDeductionPaymentDialog from "../components/ConfirmDeductionPaymentDialog";
import Labels from "../labels/Labels";

async function fetchPadDeductions() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/pad/deductions`
  );
  return response;
}

export default function PayrollDeductionsStandAlone() {
  const [payrollDeductionDetail, setPayrollDeductionDetail] = useState();
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const [infoToConfirm, setInfoToConfirm] = useState({});
  const [openConfirmDlg, setOpenConfirmDlg] = useState(false);

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.id",
        "id"
      ),
      width: 20,
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.employer",
        "Employer"
      ),
      width: 200,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.description}
            </Typography>
            <Typography
              sx={{ fontSize: 8, mb: 0, fontWeight: "bold", color: "gray" }}
              gutterBottom
            >
              {params.row.idCompany}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.status",
        "Status"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography
            sx={{
              fontSize: 12,
              mb: 0,
              fontWeight: "bold",
              color: params.row.status === "PAID" ? "green" : "orange",
            }}
            gutterBottom
          >
            {params.row.status}
          </Typography>
        );
      },
    },
    {
      field: "amount",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.amount",
        "Amount"
      ),
      width: 100,
      editable: false,
    },
    {
      field: "creationDate",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.creationDate",
        "Creation Date"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.creationDate}
          </Typography>
        );
      },
    },
    {
      field: "periodStartDate",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.periodStartDate",
        "Period Start Date"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.periodStartDate}
          </Typography>
        );
      },
    },
    {
      field: "periodEndDate",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.periodEndDate",
        "Period End Date"
      ),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.periodEndDate}
          </Typography>
        );
      },
    },
    {
      field: "viewDetails",
      headerName: Labels.getLabel(
        "payrollDeductionsStandAlone.column.actions",
        "Actions"
      ),
      width: 200,
      renderCell: (params) => (
        <div key={"action." + params.id}>
          <IconButton
            key={"view." + params.id}
            onClick={() => {
              setPayrollDeductionDetail({ ...params.row });
            }}
          >
            <VisibilityIcon />
          </IconButton>
          {params.row.status === "PENDING" ? (
            <ButtonWithIcon
              onClick={onConfirmButtonClick}
              hidden={params.row.status === "PAID"}
              color="green"
              tooltip={Labels.getLabel(
                "payrollDeductionsStandAlone.MarkAsPaid",
                "Mark as Paid"
              )}
              icon="confirm"
              record={params.row}
              text={Labels.getLabel(
                "payrollDeductionsStandAlone.MarkAsPaid",
                "Mark as Paid"
              )}
              positionTextAtRight={true}
            />
          ) : (
            ""
          )}
        </div>
      ),
    },
  ];

  const { data, isLoading } = useQuery(["pad-dedudctions"], fetchPadDeductions);

  const onConfirmButtonClick = (e, params) => {
    setInfoToConfirm(params);
    setOpenConfirmDlg(true);
  };

  const payrollDeductionDetailDialog = (event) => {
    return (
      <Dialog
        open={payrollDeductionDetail !== "undefined"}
        fullWidth
        onClose={() => {}}
        maxWidth="xl"
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ReceiptLongIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "payrollDeductionsStandAlone.DetailTitle",
                "Payroll Deduction Detail"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setPayrollDeductionDetail();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <PayrollDeductionDetails
            payrollDeductionInfo={payrollDeductionDetail}
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex">
              <Typography variant="h5">
                {Labels.getLabel(
                  "payrollDeductionsStandAlone.Title",
                  "PAD Standalone (Payroll Deduction)"
                )}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              rows={data.data}
              columns={columns}
              getRowHeight={() => 40}
              density="compact"
              disableDensitySelector
              disableColumnSelector
              disableColumnFilter
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      {payrollDeductionDetail ? payrollDeductionDetailDialog() : ""}
      {openConfirmDlg ? (
        <ConfirmDeductionPaymentDialog
          action="CONFIRM"
          open={openConfirmDlg}
          setOpen={setOpenConfirmDlg}
          dialogTitle={Labels.getLabel(
            "payrollDeductionsStandAlone.ConfirmTitle",
            "Confirm the Payroll Deduction is Paid"
          )}
          dialogContentText={Labels.getLabel(
            "payrollDeductionsStandAlone.ConfirmContent",
            "To confirm the payroll deduction is paid, type invoice # or transaction # and click CONFIRM button."
          )}
          infoToConfirm={infoToConfirm}
        />
      ) : (
        ""
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
